import React, { useState, useMemo, useEffect } from 'react';
import {FaArrowLeft, FaChevronDown, FaChevronUp, FaPlus, FaTrash} from 'react-icons/fa';
import InfoBanner from '../InfoBanner';
import axiosInstance from '../../utility/axiosInstance';
import { useToast } from '../../components/ToastContext';
import { useLimitedTrial } from '../../hooks/useLimitedTrial';
import { handleAxiosError } from '../../helpers/handleAxiosError';

interface DNSDetailsViewProps {
    proxyHost: any;
    onBack: () => void;
    onRefresh: () => void;
    isBillingInvalid?: boolean;
}

const DNSDetailsView: React.FC<DNSDetailsViewProps> = ({ proxyHost: initialProxyHost, onBack, onRefresh, isBillingInvalid }) => {
    const { showToast } = useToast();
    const isTrial = useLimitedTrial();
    
    const normalizeProxyHost = (ph: any) => {
        if (!ph) return ph;
        const normalized = { ...ph };
        const booleanFields = [
            'enabled', 'block_exploits', 'caching_enabled', 
            'allow_websocket_upgrade', 'http2_support', 
            'hsts_enabled', 'hsts_subdomains', 'ssl_forced',
            'use_openappsec'
        ];
        booleanFields.forEach(field => {
            if (normalized[field] !== undefined) {
                normalized[field] = normalized[field] === 1 || normalized[field] === true;
            }
        });

        if (normalized.locations && Array.isArray(normalized.locations)) {
            normalized.locations = normalized.locations.map((loc: any) => ({
                ...loc,
                use_openappsec: loc.use_openappsec === 1 || loc.use_openappsec === true,
            }));
        } else {
            normalized.locations = [];
        }

        return normalized;
    };

    const normalizedInitial = useMemo(() => normalizeProxyHost(initialProxyHost), [initialProxyHost]);
    const [proxyHost, setProxyHost] = useState(normalizedInitial);
    const [loading, setLoading] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [locationsOpen, setLocationsOpen] = useState(false);

    useEffect(() => {
        setProxyHost(normalizedInitial);
    }, [normalizedInitial]);

    const toggleSetting = (field: string) => {
        if (field === 'enabled' && isBillingInvalid) {
            showToast("Please resolve your billing to enable this proxy host.", "error");
            return;
        }
        setProxyHost((prev: { [x: string]: any; }) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSettingChange = (field: string, value: any) => {
        setProxyHost((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const addLocation = () => {
        setProxyHost((prev: any) => ({
            ...prev,
            locations: [
                ...(prev.locations || []),
                {
                    path: '',
                    forward_scheme: 'http',
                    forward_host: prev.forward_host || '',
                    forward_port: 80,
                    use_openappsec: true,
                    openappsec_mode: 'detect-learn',
                    minimum_confidence: 'high',
                    advanced_config: ''
                }
            ]
        }));
    };

    const removeLocation = (index: number) => {
        setProxyHost((prev: any) => ({
            ...prev,
            locations: prev.locations.filter((_: any, i: number) => i !== index)
        }));
    };

    const handleLocationChange = (index: number, field: string, value: any) => {
        setProxyHost((prev: any) => {
            const newLocations = [...prev.locations];
            newLocations[index] = { ...newLocations[index], [field]: value };
            return { ...prev, locations: newLocations };
        });
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.put(`/v1/dns/proxy-hosts/${proxyHost.id}`, proxyHost);
            setProxyHost(normalizeProxyHost(response.data));
            showToast('Changes saved successfully!', 'success');
            onRefresh();
        } catch (err) {
            // The server's own reason when it REFUSED the change (a 4xx: a location target, a domain not verified); a
            // server error's text is not for the customer (the fix review).
            const status = (err as any)?.response?.status;
            showToast(status >= 400 && status < 500 ? `Failed to save changes: ${handleAxiosError(err)}` : 'Failed to save changes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const isChanged = useMemo(() => JSON.stringify(proxyHost) !== JSON.stringify(normalizedInitial), [proxyHost, normalizedInitial]);

    return (
        <div className="dns-details-view">
            <div className="view-header">
                <button onClick={onBack} className="back-button">
                    <FaArrowLeft /> Back to List
                </button>
                <button 
                    onClick={handleSave} 
                    className="save-button"
                    disabled={loading || !isChanged}
                >
                    {loading ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
            <InfoBanner id="dns-details-public-access" style={{ marginBottom: '1rem' }}>
                <span>If you disable public access, you can still access your vm from the same network via internal IP shown in Instance Details</span>
            </InfoBanner>
            <div className="details-card">
                <div className="details-header">
                    <h2>DNS Details: {proxyHost.domain_names?.[0]}</h2>
                    <div className="status-container">
                        <span>Status: </span>
                        <button 
                            className={`status-toggle ${proxyHost.enabled ? 'enabled' : 'disabled'} ${isBillingInvalid ? 'billing-disabled' : ''}`}
                            onClick={() => toggleSetting('enabled')}
                            disabled={loading}
                        >
                            {proxyHost.enabled ? 'Enabled' : 'Disabled'}
                        </button>
                    </div>
                </div>

                <div className="settings-section">
                    <h3>Proxy Settings</h3>
                    <div className="settings-grid">
                        {[
                            { label: 'Block Exploits', field: 'block_exploits' },
                            { label: 'Caching Enabled', field: 'caching_enabled' },
                            { label: 'Websocket Support', field: 'allow_websocket_upgrade' },
                            { label: 'HTTP/2 Support', field: 'http2_support' },
                            { label: 'HSTS Enabled', field: 'hsts_enabled' },
                            { label: 'HSTS Subdomains', field: 'hsts_subdomains' },
                            { label: 'Force SSL', field: 'ssl_forced' },
                        ].map(s => (
                            <div key={s.field} className="setting-card">
                                <label>{s.label}</label>
                                <label className="switch">
                                    <input 
                                        type="checkbox" 
                                        checked={!!proxyHost[s.field]} 
                                        onChange={() => toggleSetting(s.field)}
                                        disabled={loading}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="advanced-section">
                    <div className="advanced-header" onClick={() => setAdvancedOpen(!advancedOpen)}>
                        <h3>Advanced Settings</h3>
                        {advancedOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    {advancedOpen && isTrial && (
                        <div className="advanced-content">
                            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                                Advanced security (machine-learning threat prevention) is not available on the limited trial.
                                Connect a card to enable it.
                            </p>
                        </div>
                    )}
                    {advancedOpen && !isTrial && (
                        <div className="advanced-content">
                            <div className="setting-card">
                                <label>Enable</label>
                                <label className="switch">
                                    <input 
                                        type="checkbox" 
                                        checked={!!proxyHost.use_openappsec} 
                                        onChange={() => toggleSetting('use_openappsec')}
                                        disabled={loading}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                            <div className="setting-group">
                                <label>Threat Prevention Policy</label>
                                <select 
                                    value={proxyHost.openappsec_mode} 
                                    onChange={(e) => handleSettingChange('openappsec_mode', e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="inactive">Inactive</option>
                                    <option value="detect-learn">Detect-Learn</option>
                                    <option value="prevent">Prevent</option>
                                </select>
                            </div>
                            <div className="setting-group">
                                <label>Minimum Confidence</label>
                                <select 
                                    value={proxyHost.minimum_confidence} 
                                    onChange={(e) => handleSettingChange('minimum_confidence', e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                <div className="locations-section">
                    <div className="locations-header" onClick={() => setLocationsOpen(!locationsOpen)}>
                        <h3>Custom Locations</h3>
                        {locationsOpen ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    {locationsOpen && (
                        <div className="locations-content">
                            {(proxyHost.locations || []).map((loc: any, idx: number) => (
                                <div key={idx} className="location-card">
                                    <button className="remove-button" onClick={() => removeLocation(idx)}>
                                        <FaTrash />
                                    </button>
                                    <div className="location-fields-grid">
                                        <div className="setting-group">
                                            <label>Path</label>
                                            <input 
                                                type="text" 
                                                value={loc.path} 
                                                onChange={(e) => handleLocationChange(idx, 'path', e.target.value)}
                                                placeholder="/path"
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="setting-group">
                                            <label>Scheme</label>
                                            <select 
                                                value={loc.forward_scheme} 
                                                onChange={(e) => handleLocationChange(idx, 'forward_scheme', e.target.value)}
                                                disabled={loading}
                                            >
                                                <option value="http">http</option>
                                                <option value="https">https</option>
                                            </select>
                                        </div>
                                        <div className="setting-group">
                                            <label>Forward Host</label>
                                            <input 
                                                type="text" 
                                                value={loc.forward_host} 
                                                onChange={(e) => handleLocationChange(idx, 'forward_host', e.target.value)}
                                                placeholder="hostname"
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="setting-group">
                                            <label>Forward Port</label>
                                            <input 
                                                type="number" 
                                                value={loc.forward_port} 
                                                onChange={(e) => handleLocationChange(idx, 'forward_port', parseInt(e.target.value))}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="location-openappsec">
                                        <div className="openappsec-toggle-row">
                                            <label>Enable Additional Security</label>
                                            <label className="switch">
                                                <input 
                                                    type="checkbox" 
                                                    checked={!!loc.use_openappsec} 
                                                    onChange={() => handleLocationChange(idx, 'use_openappsec', !loc.use_openappsec)}
                                                    disabled={loading}
                                                />
                                                <span className="slider round"></span>
                                            </label>
                                        </div>
                                        {loc.use_openappsec && (
                                            <div className="openappsec-settings">
                                                <div className="setting-group">
                                                    <label>Mode</label>
                                                    <select 
                                                        value={loc.openappsec_mode} 
                                                        onChange={(e) => handleLocationChange(idx, 'openappsec_mode', e.target.value)}
                                                        disabled={loading}
                                                    >
                                                        <option value="inactive">Inactive</option>
                                                        <option value="detect-learn">Detect-Learn</option>
                                                        <option value="prevent">Prevent</option>
                                                    </select>
                                                </div>
                                                <div className="setting-group">
                                                    <label>Confidence</label>
                                                    <select 
                                                        value={loc.minimum_confidence} 
                                                        onChange={(e) => handleLocationChange(idx, 'minimum_confidence', e.target.value)}
                                                        disabled={loading}
                                                    >
                                                        <option value="low">Low</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="high">High</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button className="add-location-button" onClick={addLocation} disabled={loading}>
                                <FaPlus /> Add Location
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DNSDetailsView;
